import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useSessionContext } from '@/contexts/SessionContext';

export const trackBlogView = async (
  blogPostId: string,
  userId?: string,
  sessionKey?: string,
) => {
  const userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : undefined;

  if (!userId && !sessionKey) {
    return { tracked: false, inserted: false };
  }

  // First, try to UPDATE existing view record's timestamp
  let updateQuery = supabase
    .from('views')
    .update({
      created_at: new Date().toISOString(),
      user_agent: userAgent,
    })
    .eq('blog_post_id', blogPostId);

  // Apply the correct filter based on auth state
  if (userId) {
    updateQuery = updateQuery.eq('user_id', userId);
  } else {
    updateQuery = updateQuery.eq('session_key', sessionKey);
  }

  const { data: updateData, error: updateError } = await updateQuery.select();

  if (updateError) throw updateError;

  // If no record was updated, insert a new one (first-time view)
  if (!updateData || updateData.length === 0) {
    const { error: insertError } = await supabase.from('views').insert({
      blog_post_id: blogPostId,
      user_id: userId || null,
      session_key: sessionKey || null,
      ip_address: null,
      user_agent: userAgent,
      association_status: 'pending',
    });

    if (insertError) throw insertError;

    return { tracked: true, inserted: true };
  }

  return { tracked: true, inserted: false };
};

export const useViewCount = (blogPostId: string) => {
  const { user } = useAuth();
  const { sessionKey } = useSessionContext();
  const [viewCount, setViewCount] = useState(0);
  const [lastViewTime, setLastViewTime] = useState<Date | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchViewCount = async () => {
    // Skip fetch if no blogPostId (used in list view with pre-fetched metrics)
    if (!blogPostId) {
      setLoading(false);
      return;
    }
    
    try {
      const { data, error } = await supabase
        .rpc('get_blog_post_view_count', { post_id: blogPostId });

      if (error) throw error;

      setViewCount(data || 0);
    } catch (error: any) {
      console.error('Error fetching view count:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchLastViewTime = async () => {
    // Get the most recent view timestamp for this post by this user/session
    try {
      let query = supabase
        .from('views')
        .select('created_at')
        .eq('blog_post_id', blogPostId)
        .order('created_at', { ascending: false })
        .limit(1);

      // Filter by user_id or session_key
      if (user?.id) {
        query = query.eq('user_id', user.id);
      } else if (sessionKey) {
        query = query.eq('session_key', sessionKey);
      } else {
        // No user or session, can't fetch
        return;
      }

      const { data, error } = await query.maybeSingle();

      if (error) throw error;

      if (data) {
        setLastViewTime(new Date(data.created_at));
      }
    } catch (error: any) {
      console.error('Error fetching last view time:', error);
    }
  };

  const trackView = async (sessionKeyParam?: string) => {
    const effectiveSessionKey = sessionKeyParam || sessionKey;

    if (!blogPostId) return;

    try {
      const { tracked, inserted } = await trackBlogView(blogPostId, user?.id, effectiveSessionKey);

      if (!tracked) return;

      setLastViewTime(new Date());

      if (inserted) {
        setViewCount((prev) => prev + 1);
      }
    } catch (error: any) {
      console.error('Error tracking view:', error);
    }
  };

  useEffect(() => {
    if (blogPostId) {
      fetchViewCount();
      fetchLastViewTime();
    }
  }, [blogPostId, user?.id, sessionKey]);

  return {
    viewCount,
    lastViewTime,
    loading,
    trackView,
    refetch: fetchViewCount
  };
};
